<template>
  <Teleport to="body">
    <div
        v-if="showModal"
        class="modal-overlay"
        @click.self="closeReservationModal"
    >
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">
            <font-awesome-icon icon="fa-solid fa-calendar-plus" class="me-2"/>
            Potwierdzenie rezerwacji
          </h5>
          <button
              type="button"
              class="btn-close"
              :class="{ 'btn-close-white': isDarkMode }"
              @click="closeReservationModal"
              :disabled="isSubmitting"
          ></button>
        </div>

        <div v-if="!reservationSuccess" class="modal-body">
          <!-- Summary list (similar to ReservationDetails) -->
          <div class="details-list mb-3">
            <div class="detail-item">
              <div class="detail-icon">
                <font-awesome-icon icon="fa-solid fa-bullseye"/>
              </div>
              <div class="detail-content">
                <label>Stanowisko</label>
                <span>{{ spaceName }}</span>
              </div>
            </div>

            <div class="detail-item">
              <div class="detail-icon">
                <font-awesome-icon icon="fa-solid fa-calendar-day"/>
              </div>
              <div class="detail-content">
                <label>Data</label>
                <span>{{ labelReservationDate }}</span>
              </div>
            </div>

            <div class="detail-item">
              <div class="detail-icon">
                <font-awesome-icon icon="fa-solid fa-clock"/>
              </div>
              <div class="detail-content">
                <label>Czas trwania</label>
                <span>{{ timeSlots[selectedRange ? selectedRange.from : 0] }} - {{ selectedRange ? getEndSlotTime(selectedRange.to) : '' }}</span>
              </div>
            </div>

            <div class="detail-item">
              <div class="detail-icon">
                <font-awesome-icon icon="fa-solid fa-money-bill-wave"/>
              </div>
              <div class="detail-content">
                <label>Kwota</label>
                <span class="fw-bold text-success">{{ reservationPrice }} zł</span>
              </div>
            </div>
          </div>

          <!-- Phone number autocomplete field -->
          <div class="mt-3 position-relative" ref="autocompleteWrapper">
            <div class="d-flex justify-content-between align-items-center mb-1">
              <label for="phoneNumberInput" class="form-label small m-0 fw-bold">
                <font-awesome-icon icon="fa-solid fa-phone" class="me-1 text-warning"/>
                Numer telefonu
              </label>
              <span
                  v-if="phoneNumberQuery.trim().length >= 3 && usersLoaded"
                  class="badge"
                  :class="existingUser ? 'bg-success' : 'bg-secondary'"
              >
                    {{ existingUser ? 'Istniejący użytkownik' : 'Gość' }}
                  </span>
            </div>
            <div class="input-group">
              <input
                  id="phoneNumberInput"
                  type="text"
                  class="form-control form-control-sm border-secondary dropdown-toggle"
                  placeholder="Wpisz min. 3 cyfry..."
                  v-model="phoneNumberQuery"
                  @input="onPhoneNumberInput"
                  @click="toggleDropdown"
                  :disabled="isSubmitting"
                  autocomplete="off"
              />
              <span v-if="isFetchingUsers" class="input-group-text border-secondary bg-transparent">
                    <span class="spinner-border spinner-border-sm" role="status"></span>
                  </span>
            </div>

            <!-- Dropdown list -->
            <ul
                v-if="showDropdown && filteredUsers.length"
                class="dropdown-menu show w-100 position-absolute border-secondary mt-1 shadow overflow-auto"
                style="max-height: 180px; z-index: 1060;"
            >
              <li v-for="user in filteredUsers" :key="user.id || user.uuid">
                <button
                    type="button"
                    class="dropdown-item py-2 px-3 border-bottom border-secondary"
                    @click="selectUser(user)"
                >
                  <div class="fw-bold">{{ user.phone || 'Brak telefonu' }}</div>
                  <small class="text-secondary" v-if="user.email">
                    {{ user.email }}
                  </small>
                </button>
              </li>
            </ul>

            <!-- Assign to user checkbox -->
            <div v-if="matchedUser || selectedUser" class="form-check mt-2">
              <input
                  class="form-check-input"
                  type="checkbox"
                  id="assignToUserCheckbox"
                  v-model="assignToUser"
                  :disabled="isSubmitting"
              />
              <label class="form-check-label small" for="assignToUserCheckbox">
                Przypisz do użytkownika
              </label>
            </div>
          </div>

          <!-- Payment methods -->
          <div class="mt-3">
            <label class="form-label small d-block mb-2 fw-bold">
              <font-awesome-icon icon="fa-solid fa-credit-card" class="me-1 text-warning"/>
              Metoda płatności
            </label>
            <div
                v-for="method in availablePaymentMethods"
                :key="method"
                class="form-check payment-radio-item mb-2 p-0"
            >
              <input
                  class="btn-check"
                  type="radio"
                  :name="'paymentMethod'"
                  :id="'payment_' + method"
                  :value="method"
                  v-model="selectedPaymentMethod"
                  :disabled="isSubmitting"
                  autocomplete="off"
              >
              <label
                  class="btn btn-outline-secondary w-100 text-start d-flex align-items-center py-2 px-3"
                  :for="'payment_' + method"
              >
                <font-awesome-icon
                    :icon="selectedPaymentMethod === method ? 'fa-solid fa-circle-dot' : 'fa-regular fa-circle'"
                    class="me-2"
                    :class="selectedPaymentMethod === method ? 'text-warning' : 'text-secondary'"
                />
                {{ formatPaymentMethod(method) }}
              </label>
            </div>
          </div>

          <!-- Note field -->
          <div class="mt-3">
            <div class="d-flex justify-content-between align-items-center mb-1">
              <label for="reservationNote" class="form-label small m-0 fw-bold">
                <font-awesome-icon icon="fa-solid fa-note-sticky" class="me-1 text-warning"/>
                Notatka
              </label>
              <small class="text-secondary">{{ note.length }}/500</small>
            </div>
            <textarea
                id="reservationNote"
                class="form-control form-control-sm border-secondary"
                rows="2"
                maxlength="500"
                placeholder="Opcjonalna notatka..."
                v-model="note"
                :disabled="isSubmitting"
            ></textarea>
          </div>

          <div v-if="submissionError" class="alert alert-danger mt-3 mb-0">
            {{ submissionError }}
          </div>
        </div>

        <div v-else class="modal-body text-center py-5">
          <font-awesome-icon icon="fa-solid fa-circle-check" class="text-success mb-3" size="4x"/>
          <h4 class="fw-bold">Rezerwacja udana!</h4>
          <p class="text-secondary mb-0">Twoje miejsce zostało zarezerwowane.</p>
        </div>

        <div v-if="!reservationSuccess" class="modal-footer border-0">
          <button
              type="button"
              class="btn btn-outline-secondary px-4"
              @click="closeReservationModal"
              :disabled="isSubmitting"
          >
            <font-awesome-icon icon="fa-solid fa-xmark" class="me-1"/>
            Anuluj
          </button>
          <button
              type="button"
              class="btn btn-primary px-4 fw-bold"
              @click="confirmReservation"
              :disabled="isSubmitting"
          >
            <span v-if="isSubmitting" class="spinner-border spinner-border-sm me-2"></span>
            <font-awesome-icon v-else icon="fa-solid fa-check" class="me-1"/>
            Zarezerwuj
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script>
import {fetchWrapper} from "@/wrappers/api/fetchWrapper";
import {FontAwesomeIcon} from "@fortawesome/vue-fontawesome";
import {usePricesStore} from "@/stores/prices";

export default {
  name: 'CreateReservation',
  components: {
    FontAwesomeIcon
  },
  props: {
    showModal: {
      type: Boolean,
      required: true
    },
    selectedRange: Object,
    spaceName: {
      type: String,
      default: ''
    },
    spaces: Array,
    labelReservationDate: String,
    timeSlots: Array,
    availablePaymentMethods: Array,
  },
  emits: ['close', 'reservation-created', 'update-selection'],
  setup() {
    const pricesStore = usePricesStore();
    return {pricesStore};
  },
  data() {
    return {
      isSubmitting: false,
      submissionError: null,
      reservationSuccess: false,
      selectedPaymentMethod: 'pay_at_location',
      phoneNumberQuery: '',
      selectedUser: null,
      assignToUser: false,
      note: '',
      fetchedUsers: [],
      usersLoaded: false,
      isFetchingUsers: false,
      showDropdown: false,
      searchDebounceTimer: null
    };
  },
  computed: {
    isDarkMode() {
      return document.documentElement.getAttribute('data-bs-theme') === 'dark';
    },
    matchedUser() {
      if (!this.phoneNumberQuery || !this.fetchedUsers.length) return null;
      const query = this.phoneNumberQuery.trim();
      return this.fetchedUsers.find(u => u.phone && String(u.phone).trim() === query) || null;
    },
    existingUser() {
      return !!this.matchedUser || !!this.selectedUser;
    },
    filteredUsers() {
      if (!this.phoneNumberQuery || this.phoneNumberQuery.length < 3) return [];
      const query = this.phoneNumberQuery.toLowerCase().trim();
      return this.fetchedUsers.filter(u => {
        const phone = String(u.phone || '').toLowerCase();
        const email = String(u.email || '').toLowerCase();
        return phone.includes(query) || email.includes(query);
      });
    },
    reservationPrice() {
      if (!this.selectedRange || !this.pricesStore) return 0;

      const start = this.timeSlots[this.selectedRange.from];
      const end = this.getEndSlotTime(this.selectedRange.to);

      const [startH, startM] = start.split(':').map(Number);
      const [endH, endM] = end.split(':').map(Number);

      let totalMinutes = (endH * 60 + endM) - (startH * 60 + startM);

      if (totalMinutes < 0) totalMinutes += 1440;

      const h = Math.floor(totalMinutes / 60);
      const m = totalMinutes % 60;
      const duration = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;

      return this.pricesStore.calculatePrice(1, duration) || 0;
    }
  },
  watch: {
    showModal(newVal) {
      if (newVal) {
        this.resetForm();
        document.addEventListener('click', this.handleClickOutside);
      } else {
        document.removeEventListener('click', this.handleClickOutside);
      }
    }
  },
  mounted() {
    if (this.showModal) {
      document.addEventListener('click', this.handleClickOutside);
    }
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside);
  },
  methods: {
    resetForm() {
      this.phoneNumberQuery = '';
      this.selectedUser = null;
      this.assignToUser = false;
      this.note = '';
      this.fetchedUsers = [];
      this.usersLoaded = false;
      this.showDropdown = false;
      this.submissionError = null;
    },

    toggleDropdown() {
      if (this.phoneNumberQuery.trim().length >= 3) {
        if (!this.usersLoaded && !this.isFetchingUsers) {
          this.fetchUsersByPhone();
        } else {
          // Only toggle dropdown if not an exact match or if closing
          if (this.matchedUser && this.showDropdown) {
            this.showDropdown = false;
          } else if (!this.matchedUser) {
            this.showDropdown = !this.showDropdown;
          }
        }
      }
    },

    handleClickOutside(event) {
      const wrapper = this.$refs.autocompleteWrapper;
      if (wrapper && !wrapper.contains(event.target)) {
        this.showDropdown = false;
      }
    },

    /* Actions */
    async confirmReservation() {
      if (!this.selectedRange || this.isSubmitting) return

      this.isSubmitting = true;
      this.submissionError = null;

      try {
        // Extract YYYY-MM-DD from label (e.g., "Poniedziałek, 06.07.2026")
        const dateMatch = this.labelReservationDate.match(/(\d{2})\.(\d{2})\.(\d{4})/);
        if (!dateMatch) throw new Error("Nieprawidłowy format daty");

        const dateStr = `${dateMatch[3]}-${dateMatch[2]}-${dateMatch[1]}`;
        const startTime = this.timeSlots[this.selectedRange.from];
        const endTime = this.getEndSlotTime(this.selectedRange.to);

        const activeUser = this.matchedUser || this.selectedUser;

        const body = {
          space_id: this.spaces[this.selectedRange.spaceIdx].id,
          start_date: `${dateStr}T${startTime}:00+02:00`,
          end_date: `${dateStr}T${endTime}:00+02:00`,
          paid: this.selectedPaymentMethod,
          phone_number: this.phoneNumberQuery,
          reserved_for: (this.assignToUser && activeUser) ? activeUser.id : null,
          note: this.note
        };

        await fetchWrapper.post('/admin/reservations/space/create', body);

        this.reservationSuccess = true;
        this.$emit('update-selection', {label: null, firstCell: null, selectedRange: null});
        this.$emit('reservation-created'); // Notify parent to refresh data in background

        // Show success confirmation screen for 3 seconds before closing
        setTimeout(() => {
          this.reservationSuccess = false;
          this.$emit('close');
        }, 3000);

      } catch (err) {
        this.submissionError = err;
      } finally {
        this.isSubmitting = false;
      }
    },

    closeReservationModal() {
      this.showDropdown = false;
      this.$emit('close');
    },

    /* Autocomplete phone methods */
    onPhoneNumberInput() {
      this.selectedUser = null;
      this.assignToUser = false;
      const query = this.phoneNumberQuery.trim();

      if (query.length < 3) {
        this.showDropdown = false;
        return;
      }

      // If exact match found, close dropdown
      if (this.matchedUser) {
        this.showDropdown = false;
        return;
      }

      // If users were already fetched for this modal session, filter locally
      if (this.usersLoaded) {
        this.showDropdown = this.filteredUsers.length > 0;
        return;
      }

      if (this.searchDebounceTimer) clearTimeout(this.searchDebounceTimer);
      this.searchDebounceTimer = setTimeout(() => {
        this.fetchUsersByPhone();
      }, 300);
    },

    async fetchUsersByPhone() {
      if (this.usersLoaded || this.isFetchingUsers) return;

      this.isFetchingUsers = true;
      try {
        const response = await fetchWrapper.get('/admin/users');
        this.fetchedUsers = response.activeUsers || response || [];
        this.usersLoaded = true;

        // If query matches a user's phone exactly, do not show dropdown
        if (this.matchedUser) {
          this.showDropdown = false;
        } else {
          this.showDropdown = this.filteredUsers.length > 0;
        }
      } catch (err) {
        console.warn('Nie udało się pobrać użytkowników:', err);
        this.fetchedUsers = [];
        this.showDropdown = false;
      } finally {
        this.isFetchingUsers = false;
      }
    },

    selectUser(user) {
      this.selectedUser = user;
      this.phoneNumberQuery = user.phone || '';
      this.showDropdown = false;
    },

    /* Formats */
    formatPaymentMethod(method) {
      const translations = {
        'pay_at_location': 'Płatność na miejscu',
        'p24': 'Przelewy24 (Online)'
      };
      return translations[method] || method;
    },
    getEndSlotTime(slotIdx) {
      // If there is a next slot, that is our end time
      if (this.timeSlots[slotIdx + 1]) {
        return this.timeSlots[slotIdx + 1];
      }

      // Otherwise, calculate +30 mins from the last slot manually
      const lastSlot = this.timeSlots[slotIdx]; // e.g., "21:30"
      const [h, m] = lastSlot.split(':').map(Number);
      const date = new Date();
      date.setHours(h, m + 30, 0, 0);

      const hh = String(date.getHours()).padStart(2, '0');
      const mm = String(date.getMinutes()).padStart(2, '0');
      return `${hh}:${mm}`;
    }
  }
}
</script>

<style scoped>
/* ── Modal overlay ──────────────────────────────────────── */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
  padding: 1rem;
}

.modal-content {
  background-color: var(--bs-body-bg);
  border: 1px solid var(--bs-border-color);
  border-radius: 1rem;
  max-width: 500px;
  width: 100%;
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.2);
  overflow: hidden;
}

.modal-header {
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid var(--bs-border-color);
  background-color: var(--bs-tertiary-bg);
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-title {
  font-size: 1.1rem;
  font-weight: 700;
  color: #6596d4;
  margin: 0;
  display: flex;
  align-items: center;
}

.modal-body {
  padding: 1.5rem;
}

.details-list {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.detail-item {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.detail-icon {
  width: 38px;
  height: 38px;
  background-color: var(--bs-secondary-bg);
  border-radius: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #f0c040;
  flex-shrink: 0;
  font-size: 1.1rem;
}

.detail-content {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.detail-content label {
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--bs-secondary-color);
  font-weight: 700;
  margin-bottom: 0.1rem;
}

.detail-content span {
  font-size: 0.95rem;
  color: var(--bs-body-color);
  font-weight: 500;
  word-break: break-word;
}

.modal-footer {
  padding: 1rem 1.5rem;
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
}

.btn-primary {
  background-color: #f0c040 !important;
  border: none !important;
  color: #000 !important;
}

.btn-primary:hover {
  background-color: #ffd966 !important;
}

/* ── Phone Autocomplete styles for Light / Dark themes ──── */
.form-label,
.form-check-label {
  color: var(--bs-body-color) !important;
}

/* Light / Dark adjustments */
[data-bs-theme="light"] .modal-content {
  box-shadow: 0 15px 50px rgba(0, 0, 0, 0.1);
}

[data-bs-theme="light"] .detail-icon {
  background-color: #f8f9fa;
}

/* Light Theme */
[data-bs-theme="light"] #phoneNumberInput,
[data-bs-theme="light"] #reservationNote {
  background-color: var(--bs-body-bg) !important;
  color: var(--bs-body-color) !important;
  border-color: var(--bs-border-color) !important;
}

[data-bs-theme="light"] .dropdown-menu {
  background-color: var(--bs-body-bg) !important;
  border-color: var(--bs-border-color) !important;
}

[data-bs-theme="light"] .dropdown-item {
  color: var(--bs-body-color) !important;
  border-color: var(--bs-border-color) !important;
}

[data-bs-theme="light"] .dropdown-item:hover {
  background-color: var(--bs-tertiary-bg) !important;
}

[data-bs-theme="dark"] #phoneNumberInput,
[data-bs-theme="dark"] #reservationNote {
  background-color: #212529 !important;
  color: #fff !important;
  border-color: #495057 !important;
}

[data-bs-theme="dark"] .dropdown-menu {
  background-color: #212529 !important;
  border-color: #495057 !important;
}

[data-bs-theme="dark"] .dropdown-item {
  color: #fff !important;
  border-color: #343a40 !important;
}

[data-bs-theme="dark"] .dropdown-item:hover {
  background-color: #343a40 !important;
}

@media (max-width: 576px) {
  .modal-content {
    border-radius: 0.75rem;
  }

  .modal-header, .modal-body, .modal-footer {
    padding: 1rem;
  }
}
</style>